import { TicketSaleStatus as AmbassadorTicketSaleStatus, } from '@wix/ambassador-wix-events-web/types';
import { TaxType, TicketSaleStatus } from '@wix/events-types';
import { WIX_EVENTS_TICKET_DEFINITION_FQDN, WIX_EVENTS_TICKET_DEFINITION_FQDN_ALL_VERSIONS } from '..';
import { WIX_FEE_RATE } from '../constants/event';
import { getFormattedMoney } from '../price/formatter';
import { getMoney } from '../price/utils';
import { getPreliminaryTax, isFeeAdded, isTaxAdded } from './checkout';
export const calculateTicketPriceBreakDown = ({ ticketPrice, feeType, taxConfig, isDonation = false, getFormattedMoneyFunction = getFormattedMoney, }) => {
    const { currency } = ticketPrice;
    const feeAdded = isFeeAdded(feeType);
    const taxIncluded = taxConfig?.type === TaxType.INCLUDED;
    const originalTicketPrice = Number(ticketPrice.amount || 0);
    let ticketPriceAmount = originalTicketPrice;
    const tax = getPreliminaryTax(taxConfig, ticketPriceAmount, currency, isDonation);
    const taxAmount = getTaxAmount(tax);
    ticketPriceAmount = ticketPriceAfterTax(ticketPriceAmount, tax);
    const feeAmount = Number((ticketPriceAmount * WIX_FEE_RATE).toFixed(2));
    const totalBuyerPaysAmount = feeAdded ? ticketPriceAmount + feeAmount : ticketPriceAmount;
    const getPaidAmount = feeAdded ? ticketPriceAmount : totalBuyerPaysAmount - feeAmount;
    const totalBuyerPays = getFormattedMoneyFunction(getMoney(totalBuyerPaysAmount, currency));
    const getPaid = getFormattedMoneyFunction(getMoney(getPaidAmount, currency));
    const fee = getFormattedMoneyFunction(getMoney(feeAmount, currency));
    const taxFormatted = tax ? getFormattedMoneyFunction(tax.amount) : null;
    const formattedTicketPrice = getFormattedMoneyFunction(taxIncluded ? getMoney(originalTicketPrice - taxAmount, currency) : ticketPrice);
    return {
        totalBuyerPays,
        fee,
        getPaid,
        ticketPrice: formattedTicketPrice,
        tax: taxFormatted,
    };
};
export const getTicketServiceFee = (ticket, taxConfig, overridePrice, getFormattedMoneyFunction = getFormattedMoney) => {
    const ticketPrice = Number(overridePrice ?? ticket.price.amount);
    const tax = getPreliminaryTax(taxConfig, ticketPrice, getTicketDefinitionCurrency(ticket), isDonationTicketDefinition(ticket));
    const ticketPriceAmount = ticketPriceAfterTax(ticketPrice, tax);
    return getFormattedMoneyFunction({
        amount: (ticketPriceAmount * WIX_FEE_RATE).toString(),
        currency: getTicketDefinitionCurrency(ticket),
    });
};
export const getTicketTax = (ticket, taxConfig, overridePrice, getFormattedMoneyFunction = getFormattedMoney) => getFormattedMoneyFunction(getPreliminaryTax(taxConfig, Number(overridePrice ?? ticket.price.amount), getTicketDefinitionCurrency(ticket), isDonationTicketDefinition(ticket)).amount);
export const getTaxRateLabel = (taxConfig) => (taxConfig ? `${taxConfig.rate}%` : null);
const getTaxAmount = (tax) => Number(tax?.amount?.amount ?? 0);
const ticketPriceAfterTax = (ticketPrice, tax) => ticketPrice + (isTaxAdded(tax?.type) ? getTaxAmount(tax) : 0);
export const getHideNotOnSale = (ticket) => ticket.salePeriod?.hideNotOnSale ?? null;
export const shouldTicketLimitTooltipShow = (availableTicketsQuantity, selectedQuantity) => availableTicketsQuantity === 0 && !!selectedQuantity;
export const saleScheduled = (ticket) => ticket.saleStatus === TicketSaleStatus.SALE_SCHEDULED ||
    ticket.saleStatus === AmbassadorTicketSaleStatus.SALE_SCHEDULED;
export const saleStarted = (ticket) => ticket.saleStatus === TicketSaleStatus.SALE_STARTED || ticket.saleStatus === AmbassadorTicketSaleStatus.SALE_STARTED;
export const saleEnded = (ticket) => ticket.saleStatus === TicketSaleStatus.SALE_ENDED || ticket.saleStatus === AmbassadorTicketSaleStatus.SALE_ENDED;
export const shouldShowSaleEndsLabel = (ticket) => saleStarted(ticket) && !!ticket.limitPerCheckout && ticket.salePeriod;
export const saleStartedAndHasTickets = (ticket) => saleStarted(ticket) && !!ticket.limitPerCheckout;
export const saleEndedAndHasTickets = (ticket) => saleEnded(ticket) && !!ticket.limitPerCheckout;
export const saleScheduledAndHasTickets = (ticket) => saleScheduled(ticket) && !!ticket.limitPerCheckout;
export const externalIdToTicketDefinitionId = (externalId = '') => externalId.replace(WIX_EVENTS_TICKET_DEFINITION_FQDN, '').replace(WIX_EVENTS_TICKET_DEFINITION_FQDN_ALL_VERSIONS, '');
export const ticketDefinitionIdToExternalId = (ticketDefinitionId) => `${WIX_EVENTS_TICKET_DEFINITION_FQDN}${ticketDefinitionId}`;
export const ticketDefinitionIdToExternalIdAllVersions = (ticketDefinitionId) => `${WIX_EVENTS_TICKET_DEFINITION_FQDN_ALL_VERSIONS}${ticketDefinitionId}`;
export const isTicketDefinitionHidden = (definition) => definition.dashboard?.hidden || (getHideNotOnSale(definition) && !saleStarted(definition));
export const isTicketDefinitionSoldOut = (definition) => definition.dashboard.limited && definition.dashboard.unsold <= 0;
export const isFreeTicketDefinition = (definition) => {
    if (hasPricingOptions(definition)) {
        return false;
    }
    else {
        return definition.free;
    }
};
export const isDonationTicketDefinition = (definition) => definition.pricing?.minPrice !== undefined;
export const minimumDonation = (definition) => isDonationTicketDefinition(definition) ? Number(definition.pricing.minPrice.amount) : undefined;
export const getTicketDefinitionCurrency = (definition) => isDonationTicketDefinition(definition) ? definition.pricing?.minPrice?.currency : definition.price?.currency;
export const isDonationLessThanMinimum = (ticket, donation) => {
    const minimum = minimumDonation(ticket);
    return minimum && donation < minimum;
};
export const isAvailableDonationTicket = (ticket) => isDonationTicketDefinition(ticket) && saleStartedAndHasTickets(ticket);
export const hasPricingOptions = (definition) => getPricingOptions(definition).length > 0;
export const hasSinglePricingOption = (definition) => getPricingOptions(definition).length === 1;
export const getPricingOptions = (definition) => definition.pricing?.pricingOptions?.options ?? [];
export const getPricingOptionsRange = (definition) => getSelectedPricingOptionsRange(getPricingOptions(definition));
export const isPricingOptionsWithSamePrice = (definition) => {
    const { min, max } = getPricingOptionsRange(definition);
    return min.value === max.value;
};
export const getSelectedPricingOptionsRange = (pricingOptions) => pricingOptions.reduce((range, { price }) => {
    const amount = Number(price.amount);
    if (range.min === undefined || Number(range.min.amount) > amount) {
        range.min = price;
    }
    if (range.max === undefined || Number(range.max.amount) < amount) {
        range.max = price;
    }
    return range;
}, { min: undefined, max: undefined });
export const getPricingOption = (definition, optionId) => getPricingOptions(definition)?.find(({ id }) => id === optionId);
export const getPricingOptionLabel = (definition, optionId, getFormattedMoneyFunction = getFormattedMoney) => {
    const pricingOption = getPricingOption(definition, optionId);
    return `${pricingOption.name} ${getFormattedMoneyFunction(pricingOption.price)}`;
};
//# sourceMappingURL=ticket-definition.js.map